import { useCallback } from "react";
import { DEFAULT_ROTATION } from "../../canvas/config";
import { Background, Position } from "../../../types";

export const useCoreItemSettings = ({
  selectedItem,
  applyToAll,
  tracks,
  changeItem,
  selectedItemObject,
}: any) => {
  const updatePositionSettings = (position: Partial<Position>) => {
    if (!selectedItem || !selectedItemObject) return;

    if (applyToAll) {
      tracks.forEach((track: any) => {
        track.items.forEach((item: any) => {
          changeItem(item.id, (existingItem: any) => ({
            ...existingItem,
            position: {
              ...existingItem.position,
              x:
                position.x !== undefined ? position.x : existingItem.position.x,
              y:
                position.y !== undefined ? position.y : existingItem.position.y,
            },
          }));
        });
      });
    } else {
      changeItem(selectedItem, (existingItem: any) => ({
        ...existingItem,
        position: {
          ...existingItem.position,
          x: position.x !== undefined ? position.x : existingItem.position.x,
          y: position.y !== undefined ? position.y : existingItem.position.y,
        },
      }));
    }
  };

  const updateRotationSettings = (
    rotation: Partial<typeof DEFAULT_ROTATION>
  ) => {
    if (!selectedItem || !selectedItemObject) return;

    if (applyToAll) {
      tracks.forEach((track: any) => {
        track.items.forEach((item: any) => {
          changeItem(item.id, (existingItem: any) => ({
            ...existingItem,
            rotation: {
              ...existingItem.rotation,
              degrees:
                rotation.degrees !== undefined
                  ? rotation.degrees
                  : existingItem.rotation.degrees,
            },
          }));
        });
      });
    } else {
      changeItem(selectedItem, (existingItem: any) => ({
        ...existingItem,
        rotation: {
          ...existingItem.rotation,
          degrees:
            rotation.degrees !== undefined
              ? rotation.degrees
              : existingItem.rotation.degrees,
        },
      }));
    }
  };

  const updateSizeSettings = useCallback(
    (sizeUpdate: Partial<{ width: number; height: number }>) => {
      if (!selectedItem || !selectedItemObject) return;

      if (applyToAll) {
        tracks.forEach((track: any) => {
          track.items.forEach((item: any) => {
            if (item.type === "text") {
              changeItem(item.id, (existingItem: any) => {
                if (existingItem.type !== "text") return existingItem;

                return {
                  ...existingItem,
                  size: {
                    ...existingItem.size,
                    ...(sizeUpdate.width !== undefined && {
                      width: sizeUpdate.width,
                    }),
                    ...(sizeUpdate.height !== undefined && {
                      height: sizeUpdate.height,
                    }),
                  },
                };
              });
            }
          });
        });
      } else {
        if (selectedItemObject.type === "text") {
          changeItem(selectedItem, (existingItem: any) => {
            if (existingItem.type !== "text") return existingItem;

            return {
              ...existingItem,
              size: {
                ...existingItem.size,
                ...(sizeUpdate.width !== undefined && {
                  width: sizeUpdate.width,
                }),
                ...(sizeUpdate.height !== undefined && {
                  height: sizeUpdate.height,
                }),
              },
            };
          });
        }
      }
    },
    [selectedItem, selectedItemObject, applyToAll, tracks, changeItem]
  );

  const updateBackgroundSettings = (backgroundUpdate: Partial<Background>) => {
    if (!selectedItem || !selectedItemObject) return;

    const processColor = (colorResult: any): string => {
      if (colorResult && colorResult.rgb) {
        const { r, g, b, a = 1 } = colorResult.rgb;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }

      return colorResult && colorResult.hex
        ? colorResult.hex
        : String(colorResult);
    };

    let colorToApply: string | undefined = undefined;
    if (backgroundUpdate.color !== undefined) {
      if (
        typeof backgroundUpdate.color === "object" &&
        backgroundUpdate.color !== null
      ) {
        colorToApply = processColor(backgroundUpdate.color);
      } else {
        colorToApply = String(backgroundUpdate.color);
      }
    }

    const radiusToApply: number | undefined = backgroundUpdate.radius;

    const updateItemBackground = (
      existingBackground: Partial<Background> | undefined
    ): Partial<Background> => {
      const newBackground = { ...(existingBackground || {}) };

      if (colorToApply !== undefined) {
        newBackground.color = colorToApply;
      }
      if (radiusToApply !== undefined) {
        newBackground.radius = radiusToApply;
      }
      return newBackground;
    };

    if (applyToAll) {
      tracks.forEach((track: any) => {
        track.items.forEach((item: any) => {
          if (item.type === "text") {
            changeItem(item.id, (existingItem: any) => {
              if (existingItem.type !== "text") return existingItem;
              return {
                ...existingItem,
                configuration: {
                  ...existingItem.configuration,
                  background: updateItemBackground(
                    existingItem.configuration.background
                  ),
                },
              };
            });
          }
        });
      });
    } else {
      if (selectedItemObject.type === "text") {
        changeItem(selectedItem, (existingItem: any) => {
          if (existingItem.type !== "text") return existingItem;
          return {
            ...existingItem,
            configuration: {
              ...existingItem.configuration,
              background: updateItemBackground(
                existingItem.configuration.background
              ),
            },
          };
        });
      }
    }
  };

  return {
    updatePositionSettings,
    updateRotationSettings,
    updateSizeSettings,
    updateBackgroundSettings,
  };
};
